import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { useEventBus } from "src/hooks";
import { ON_SIMPLECROSS_CONFIG_CHANGE } from "src/hooks/EventBus/eventTypes";
import { fetchEvolutiveCrossConfigResult, listEvolutiveCrossConfigs } from "src/stores/actions/configs";
import { fetchFiltersList } from "src/stores/actions/evolutiveCross";
import { isNullOrEmptyArray } from "src/util/arrays";
import { Card, CardMinzd } from "../../../components";
import { cardsMap } from "../cardsMap";
import * as Ui from "../styles";
import { graphTypes } from "../types";
import EvolutiveView from "./config";
import { parseResultToDefaultEvolutive } from "./configParser";
import { formatEvolutiveCrossData } from "./formatData";
import { exportingTypes, ExportingTypesEnum } from "../exportingTypes";
import { EVOLUTIVE_CROSS_CARD } from "../cardTypes";
import { getQuestionTitlesByChartType } from "src/components/Card/CardExporting/chartExporter/questionProcessor";
import ContextualPopupExporting from "src/sections/ContextualPopupExporting";
import ContextualCardAnnotations from "src/sections/ContextualCardAnnotations";
import EvolutiveCrossChart from "src/components/Charts/EvolutiveCross";
import { fetchEvolutiveCrossChartData } from "src/stores/actions/charts";
import { annotationStrategyMap } from "src/stores/actions/annotations";
import EvolutiveCrossChartThemeManager from "src/managers/themeManager/evolutiveCrossThemeManager";

/**
 * @param data dados do grafico evolutivo
 * @param isFullScreen flag indicando se o card do gráfico evolutivo deve ser exibido
 * em fullScreen ou não.
 * @param isSomeoneInFullScreen flag indicando se existe algum card em full screen.
 * @param onFullScreenToggle callback chamado quando existe uma interação com o botão toggle de fullscreen.
 */
function CrossEvolutiveChartCard({
  // rest
  ...props
}) {
  // title
  const title = "Gráfico Evolutivo de Cruzamentos";
  // props from parent
  const {
    customerId,
    surveyId,
    surveyTitle,
    questionId,
    questionTitle,
    isFullScreen,
    isSomeoneInFullScreen,
    onFullScreenToggle,
    theme,
  } = props;

  // state
  const [chartData, setChartData] = useState(null);
  const [presets, setPresets] = useState([
    /* {value, label} */
  ]);
  // verifica se o preset esta atualizado
  const [isIncomingId, setIsIncomingId] = useState(false);
  const [evolutiveChartType, setEvolutiveChartType] = useState("line");
  const [filter, setFilter] = useState("");
  const [showConfig, setShowConfig] = useState(false);
  const [selectedEvolutiveConfigId, setSelectedEvolutiveConfigId] = useState(null);
  const [withSimpleCrossConfigs, setWithSimpleCrossConfigs] = useState(false);
  const [isMinimizedScreen, setMinimizeScreen] = useState(false);
  const [showAnnotations, setShowAnnotations] = useState(false);
  const [showExporting, setShowExporting] = useState(false);
  const [resetConfigData, setResetConfigData] = useState(false);
  // Filtros de perguntas secundárias e alternativas, é um objeto do tipo { questionId: number; alternativeId: number; }
  const [selectedFilters, setSelectedFilters] = useState(null);
  // Event bus
  const eventBus = useEventBus();

  // redux state
  const dispatch = useDispatch();

  // Configuração do gráfico
  const {
    evolutiveCross: { configuration: { configs: configurationsState, payload: configurationId }},
    evolutiveCross: { result: chartState },
    evolutiveCross: { configuration: {filter_list: filterList }},
  } = useSelector((state) => state);

  // refs
  const cardRef = useRef();
  const chartRef = React.createRef(null);

  // Gerenciador de tema de cores e fontes
  const themeManager = new EvolutiveCrossChartThemeManager(
    selectedEvolutiveConfigId,
    surveyId,
    Array.isArray(chartData) && chartData.length > 0,
    chartRef,
    theme,
    evolutiveChartType,
    EVOLUTIVE_CROSS_CARD,
  );

  const {
    colorSchemeValue,
    fontsSizes,
    colorPickerColors,
    chartsColors,
    configurationError,
  } = themeManager.getConfig();
  
  // resetData reinicializa os dados do gráfico
  const resetData = useCallback(() => {
    setChartData([]);
  }, []);
  
  // resetPresets reinicializa os dados da configuração
  const resetPresets = useCallback(() => {
    setPresets([]);
    setSelectedEvolutiveConfigId(null);
  }, []);

  const fetchEvolutiveCrossChartConfigurations = useCallback((data) => {
    const themeColors = theme.colors.charts.colors;
    const themeColorArray = data[0] ? data[0].resultados.map((alternativa, index) => themeColors[index % themeColors.length]) : [];

    fetchEvolutiveCrossChartData(
      surveyId,
      selectedEvolutiveConfigId,
      themeColorArray,
      dispatch,
    );
  }, [dispatch, selectedEvolutiveConfigId, surveyId, theme.colors.charts.colors]);

  // subscribers
  useEffect(() => {
    eventBus.subscribe(ON_SIMPLECROSS_CONFIG_CHANGE, (isReady) => {
      setWithSimpleCrossConfigs(isReady);
    });
  }, []);

  /**
   * Effects: gancho: se o id da configuração mudar uma nova requisição para filtragem de perguntas secundárias para a filtragem
   * será realizada.
   */
  useEffect(() => {
    if (selectedEvolutiveConfigId) {
      fetchFiltersList(selectedEvolutiveConfigId, dispatch);
    }
  }, [selectedEvolutiveConfigId, dispatch]);

  // gancho: alteração da listagem de configurações
  // efeito: definir com os dados da store
  useEffect(() => {
    const configKey = `${customerId}_${surveyId}_${questionId}`;
    const configList = configurationsState.payload && configurationsState.payload[configKey];
    if (!configList) {
      resetPresets();
      return;
    }

    if (!configurationsState.running) {
      setPresets(
        configList
          .sort((a, b) => a.value - b.value)
          .map((item) => ({
            label: item.descricao,
            value: item.id,
          })),
      );
      setIsIncomingId(true);
    }
  }, [configurationsState, customerId, questionId, resetPresets, surveyId]);

  // limpar os dados quando a questão for alterada
  useEffect(() => {
    resetData();
    resetPresets();
    setResetConfigData(true);
  }, [questionId, resetData, resetPresets]);

  // gancho: alteração da listagem de configurações
  // efeito: selecionar o último item selecionado ou 0
  useEffect(() => {
    if (!isNullOrEmptyArray(presets) && cardRef.current && resetConfigData) {
      const scheduledRefresh = () => {
        cardRef.current.selectPresetItemIndex(0);
      };
      setTimeout(scheduledRefresh, 0);
      setResetConfigData(false);
    }
  }, [presets, cardRef, resetConfigData]);

  // limpar os dados quando a questão for alterada
  useEffect(() => {
    resetData();
  }, [questionId, resetData]);

  // gancho: alteração na identidade da configuração
  // efeito: solicitar a listagem de dados do gráfico a partir do id da condiguração
  useEffect(() => {
    if (selectedFilters) {
      const { questionId: crossedQuestionId, alternativeId } = selectedFilters;
      fetchEvolutiveCrossConfigResult(selectedEvolutiveConfigId, crossedQuestionId, alternativeId, dispatch);
    }
  }, [dispatch, selectedFilters]);

  // ao modificar o estado dos dados de plotagem
  useEffect(() => {
    // check if has data
    const hasData = chartState
      && chartState.payload
      && selectedEvolutiveConfigId
      && !isNullOrEmptyArray(chartState.payload[selectedEvolutiveConfigId]);
    // set values
    if (hasData) {
      setChartData(parseResultToDefaultEvolutive(chartState.payload[selectedEvolutiveConfigId]));

      if (JSON.stringify(chartState.payload[selectedEvolutiveConfigId]) !== JSON.stringify(chartData)) {
        fetchEvolutiveCrossChartConfigurations(chartState.payload[selectedEvolutiveConfigId]);
      }
    } else {
      resetData();
    }
  }, [
    chartState,
    selectedEvolutiveConfigId,
    resetData,
    fetchEvolutiveCrossChartConfigurations
  ]);

  // refreshConfigs atualiza a listagem de configurações
  const refreshConfigs = useCallback(() => {
    if (surveyId && customerId && questionId) {
      resetPresets();
      listEvolutiveCrossConfigs(customerId, surveyId, questionId, dispatch);
    }
  }, [surveyId, customerId, questionId, resetPresets, dispatch]);

  // gancho: quando a triade principal (customerId, surveyId, questionId) alterar
  // efeito: listar todas as configurações para a triade principal
  useEffect(() => {
    refreshConfigs();
  }, [refreshConfigs]);


   // altera a configuração selecionada
   const changeSelectedConfig = useCallback(
    (configId) => {
      const scheduledRefresh = () => {
        cardRef.current.selectPresetItemIndex(configId === -1 ? 0 : configId);
      };
      setTimeout(scheduledRefresh, 0);
      // Verifica se o id da config não foi alterado para forçar uma atualização dos
      // dados neste caso
      if (configId === selectedEvolutiveConfigId) {
        resetPresets();
      }
    },
    [resetPresets, selectedEvolutiveConfigId],
  );


  useEffect(() => {
    if (configurationId.data && !chartState.running && isIncomingId) {
      const index = presets
        .map((pre) => pre.value)
        .indexOf(configurationId.data.id);
      changeSelectedConfig(index);
      setIsIncomingId(false);
    } 
  }, [
    changeSelectedConfig,
    chartState.running,
    configurationId.data,
    isIncomingId,
    presets,
  ]);

  // handle on open config (modal)
  function handleOpenConfig() {
    setShowConfig(true);
  }

  // handle on close config (modal)
  function handleOnCancelConfig() {
    setShowConfig(false);
  }

  // manipula a acionamento da ação de salvar configuração
  function handleSaveConfig() {
    handleOnCancelConfig();
    refreshConfigs();
  }

  // handle configuration selection (card)
  function handleSelectedConfig(configId) {
    setSelectedEvolutiveConfigId(configId);
  }

  // handle full screen toggle
  function handleFullScreenToggle(cardId, isFull) {
    if (onFullScreenToggle) onFullScreenToggle(cardId, isFull);
  }

  // handle change type
  function handleTypeChange(newType) {
    setEvolutiveChartType(newType);
  }

  // handle color scheme type change
  function handleColorSchemeChange(newType) {
    themeManager.updateColorScheme(newType);
  }

  // handle chart font type change
  function handleChartFontChange(newFontsSizes) {
    themeManager.updateFontSize(newFontsSizes);
  }

  // handle change filter value
  function handleChangeFilterValue(text) {
    setFilter(text);
  }

  // handle change evolutive cross filter (question and alternative)
  function handleChangeEvolutiveCrossFilter(filters) {
    setSelectedFilters({ ...filters });
  }

  // manipula a solicitação de minimizar o card
  function handleMinimize() {
    setMinimizeScreen(true);
  }

  function handleOpenAnnotations() {
    setShowAnnotations(true);
  }

  function handleCloseAnnotations() {
    setShowAnnotations(false);
  }

  function handleAnnotationsCancel() {
    handleCloseAnnotations();
  }

  function handleAnnotationsSave() {
    setShowAnnotations(false);
  }

  function handleAnnotationsFailure(errorType) {
    const errorStrategy = {
      fetch: () => {
        handleCloseAnnotations();
        toast.error("Houve uma falha ao recuperar sua anotação, tente novamente");
      },
      save: () => {
        toast.error("Não foi possível salvar sua anotação, tente novamente");
      },
    };
    errorStrategy[errorType]();
  }

  function handleOpenExporting() {
    setShowExporting(true);
  }

  // for render configurations
  // menu-structure: {type: [<charttypes>|<configbtn>]}
  // menu-structure: {charttypes = {type, key, selectedIndex, onSelect, data[{value, label}]}}
  // menu-structure: {configbtn = {type, key, onclick}}
  function buildMenu() {
    // result
    const result = [];
    // create chart menu
    const types = graphTypes.get("grafico-evolutivo-cruzamentos").type;
    const selectedTypeIndex = types.findIndex(
      (type) => type.value === evolutiveChartType
    );

    const colorSchemeTypes = graphTypes.get(
      "grafico-evolutivo-cruzamentos"
    ).colorSchemeType;
    const selectedColorSchemeTypeIndex = colorSchemeTypes.findIndex(
      (type) => type.value === colorSchemeValue
    );

    const chartFontTypes = graphTypes.get(
      "grafico-evolutivo-cruzamentos"
    ).chartFontsType;
    const selectedFontTypeIndex = chartFontTypes.findIndex(
      (type) => type.value === fontsSizes
    );

    const chartTypesOption = {
      type: "charttypes",
      selectedIndex: selectedTypeIndex,
      data: types,
      onSelect: handleTypeChange,
    };

    const colorSchemeOption = {
      type: "colorscheme",
      selectedIndex: selectedColorSchemeTypeIndex,
      data: colorSchemeTypes,
      onSelect: handleColorSchemeChange,
    };

    const chartFontsOption = {
      type: "chartfonts",
      selectedIndex: selectedFontTypeIndex,
      data: chartFontTypes,
      onSelect: handleChartFontChange,
    };

    const annotationsOption = {
      type: "annotations",
      onClick: handleOpenAnnotations,
    };

    const exportChartOption = {
      type: "exportChart",
      onClick: handleOpenExporting,
    };

    const configurationOption = {
      type: "configbtn",
      onClick: handleOpenConfig,
      title: "Configurar evolutivo de cruzamentos",
    };

    const options =
      chartData && chartData.length
        ? [
            chartTypesOption,
            annotationsOption,
            colorSchemeOption,
            chartFontsOption,
            exportChartOption,
            configurationOption,
          ]
        : [
            chartTypesOption,
            colorSchemeOption,
            chartFontsOption,
            configurationOption,
          ];

    result.push(
      ...options.map((option, index) => ({ ...option, key: `#key${index}` }))
    );

    // return
    return result;
  }

  /**
   * Callbacks: Verifica se o card deve ser renderizado no modo custom ou no modo default.
   * Custom: retornará true
   * Default: retornará false
   */
  const checkCustomEmptyChartRules = useCallback(() => {
    return (
      customerId &&
      surveyId &&
      questionId &&
      !withSimpleCrossConfigs
    );
  }, [customerId, surveyId, questionId, withSimpleCrossConfigs]);

  function renderChartCard() {
    return isMinimizedScreen ? (
      <CardMinzd title={title} onRestore={() => setMinimizeScreen(false)} />
    ) : (
      <Card
        minimizable
        withPresets
        presets={presets}
        ref={cardRef}
        cardId={cardsMap.graficoEvolutivoCruzamentos.cardId}
        cardTitle={title}
        cardTooltip={'Compare resultados de cruzamentos simples configurados em diferentes questionários ao longo do tempo.'}
        questionTitle={questionTitle}
        isFullScreenMode={isFullScreen}
        menu={buildMenu()}
        withData={Array.isArray(chartData) && chartData.length > 0}
        customEmptyChartRules={checkCustomEmptyChartRules()}
        loading={chartState.running}
        onMaximize={handleFullScreenToggle}
        onMinimize={handleMinimize}
        onSelectPreset={(items) => handleSelectedConfig(items[0].value)}
        theme={theme}
        withQuestionFilter
        questionFilterData={!isNullOrEmptyArray(filterList.payload) && filterList.payload}
        onChangeQuestionFilter={handleChangeEvolutiveCrossFilter}
      >
        { chartsColors && <EvolutiveCrossChart
          type={evolutiveChartType}
          data={formatEvolutiveCrossData(chartData)}
          updateEvolutiveChartColors={themeManager.updateChartColors}
          colorPickerColors={colorPickerColors}
          colors={themeManager.calculatedChartColorArray}
          chartFontSize={fontsSizes}
          legendsFilter={filter}
          error={configurationError}
          theme={theme.colors.charts}
          themeTitle={theme.title}
          chartRef={chartRef}
        />}
      </Card>
    );
  }

  function renderConfig() {
    return (
      showConfig && (
        <EvolutiveView
          isOpen
          customerId={customerId}
          surveyId={surveyId}
          questionId={questionId}
          surveyTitle={surveyTitle}
          questionTitle={questionTitle}
          onSave={handleSaveConfig}
          onCancel={handleOnCancelConfig}
          dispatch={dispatch}
        />
      )
    );
  };

  function handleExportingCancel() {
    handleCloseExporting();
  }

  function handleCloseExporting() {
    setShowExporting(false);
  }

  function renderExporting() {
    let idPerguntaCruzada = null;
    let idAlternativaCruzada = null;

    if (selectedFilters) {
      idPerguntaCruzada = selectedFilters.questionId;
      idAlternativaCruzada = selectedFilters.alternativeId
    }

    return showExporting ? (
      <ContextualPopupExporting
        onCancel={handleExportingCancel}
        types={exportingTypes}
        theme={theme}
        selectedChartType={evolutiveChartType}
        selectedCardType={EVOLUTIVE_CROSS_CARD}
        questionLabels={getQuestionTitlesByChartType(EVOLUTIVE_CROSS_CARD, questionTitle)}
        surveyId={surveyId}
        analyzeId={selectedEvolutiveConfigId}
        customerId={customerId}
        idPerguntaCruzada={idPerguntaCruzada}
        idAlternativaCruzada={idAlternativaCruzada}
      />
    ) : null;
  }

  function renderAnnotations() {
    return showAnnotations ? (
      <ContextualCardAnnotations
        strategy={annotationStrategyMap.evolutiveCross}
        customerId={customerId}
        surveyId={surveyId}
        resultKey={selectedEvolutiveConfigId}
        onCancel={handleAnnotationsCancel}
        onSave={handleAnnotationsSave}
        onFailure={handleAnnotationsFailure}
      />
    ) : null;
  }

  return (
    <Ui.CardContainer
      className="cardContainer"
      cardIsFullScreen={isFullScreen}
      someoneInFullScreen={isSomeoneInFullScreen}
    >
      {/* chart cart */}
      {renderChartCard()}
      {/* config page */}
      {renderConfig()}
      {/* chart exporting configuration modal */}
      {renderExporting()}
      {renderAnnotations()}
    </Ui.CardContainer>
  );
}

export default CrossEvolutiveChartCard;
